/**
 * IsosurfacePanel — dedicated control panel for isosurface visualization.
 *
 * Mirrors the Flow360 portal's isosurface controls: field selector,
 * searchable item list with per-item visibility, colormap toggle,
 * log scale, colormap selector, global/local range, min/max, and
 * an expandable Advanced section.
 */

import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import type { ManifestLayer } from "./manifestHelpers";
import type { UVFViewerHandle } from "./UVFViewer";

export interface IsosurfacePanelProps {
  /** The parent isosurface group layer with its children. */
  layer: ManifestLayer;
  visibility: Map<string, boolean>;
  onToggle: (modelId: string, visible: boolean) => void;
  onClose: () => void;
  viewerHandle: UVFViewerHandle | null;
  /** Notify parent when the field or colormap changes (for cross-case persistence). */
  onFieldApplied?: (fieldName: string | undefined, colormapName: string | undefined) => void;
}

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const EyeOpen = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeClosed = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const PANEL: CSSProperties = {
  position: "absolute",
  top: 8,
  left: 8,
  zIndex: 25,
  width: 260,
  maxHeight: "calc(100% - 16px)",
  overflowY: "auto",
  background: "var(--color-surface, rgba(17,17,19,0.92))",
  borderRadius: 10,
  border: "1px solid var(--gray-a4)",
  backdropFilter: "blur(12px)",
  display: "flex",
  flexDirection: "column",
  fontSize: 11,
  color: "var(--gray-12)",
};

const SECTION: CSSProperties = {
  padding: "8px 12px",
  borderBottom: "1px solid var(--gray-a3)",
};

const LABEL: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  color: "var(--gray-9)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  marginBottom: 4,
};

const SELECT: CSSProperties = {
  width: "100%",
  padding: "4px 6px",
  border: "1px solid var(--gray-a6)",
  borderRadius: 4,
  background: "var(--color-surface, #1a1a2e)",
  color: "var(--gray-12)",
  fontSize: 11,
};

const TOGGLE_ROW: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "4px 0",
};

const NUM_INPUT: CSSProperties = {
  ...SELECT,
  width: 80,
  textAlign: "center",
};

const SEARCH_INPUT: CSSProperties = {
  ...SELECT,
  marginBottom: 4,
};

function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      style={{
        width: 32,
        height: 18,
        borderRadius: 9,
        border: "none",
        background: checked ? "var(--accent-9, #3b82f6)" : "var(--gray-a5)",
        cursor: "pointer",
        position: "relative",
        padding: 0,
        transition: "background 0.15s ease",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: checked ? 16 : 2,
          width: 14,
          height: 14,
          borderRadius: "50%",
          background: "#fff",
          transition: "left 0.15s ease",
        }}
      />
    </button>
  );
}

export const IsosurfacePanel = memo(function IsosurfacePanel({
  layer,
  visibility,
  onToggle,
  onClose,
  viewerHandle,
  onFieldApplied,
}: IsosurfacePanelProps) {
  const children = layer.children ?? [];
  const childModelIds = useMemo(() => children.map((c) => c.id), [children]);

  // Field state
  const [fieldNames, setFieldNames] = useState<string[]>([]);
  const [activeField, setActiveField] = useState<string | undefined>();
  const [colormaps, setColormaps] = useState<string[]>([]);
  const [activeColormap, setActiveColormap] = useState<string | undefined>();
  const [range, setRange] = useState<[number, number] | null>(null);
  const [localMin, setLocalMin] = useState("");
  const [localMax, setLocalMax] = useState("");
  const [useColormap, setUseColormap] = useState(true);
  const [logScale, setLogScale] = useState(false);
  const [globalRange, setGlobalRange] = useState(true);
  const [contourSteps, setContourSteps] = useState(0);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rangeInitRef = useRef(false);

  // Refs for re-applying field after viewer instance changes (entity switch)
  const activeFieldRef = useRef(activeField);
  activeFieldRef.current = activeField;
  const activeColormapRef = useRef(activeColormap);
  activeColormapRef.current = activeColormap;

  // Discover fields from the first visible child model
  useEffect(() => {
    if (!viewerHandle || childModelIds.length === 0) return;

    let cancelled = false;

    const discover = (): boolean => {
      for (const mid of childModelIds) {
        const names = viewerHandle.getFieldNamesForModel(mid);
        if (names.length > 0) {
          setFieldNames(names);
          return true;
        }
      }
      return false;
    };

    const apply = () => {
      setColormaps(viewerHandle.getColormaps());
      const field = activeFieldRef.current;
      if (field) {
        viewerHandle.setFieldForModels(childModelIds, field);
        const cmap = activeColormapRef.current;
        if (cmap) viewerHandle.setColormapForModels(childModelIds, cmap);
        const autoRange = viewerHandle.getFieldRangeForModels(childModelIds);
        if (autoRange) setRange(autoRange);
      }
    };

    if (discover()) {
      apply();
    } else {
      let attempts = 0;
      const poll = () => {
        if (cancelled || attempts >= 12) return;
        attempts++;
        setTimeout(() => {
          if (cancelled) return;
          if (discover()) {
            apply();
          } else {
            poll();
          }
        }, 500);
      };
      poll();
    }

    return () => { cancelled = true; };
  }, [viewerHandle, childModelIds]);

  // Sync local range inputs when range changes
  useEffect(() => {
    if (range) {
      setLocalMin(range[0].toPrecision(4));
      setLocalMax(range[1].toPrecision(4));
      rangeInitRef.current = true;
    }
  }, [range?.[0], range?.[1]]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyField = useCallback(
    (fieldName: string | undefined) => {
      if (!viewerHandle) return;
      setActiveField(fieldName);
      viewerHandle.setFieldForModels(childModelIds, fieldName);
      if (fieldName) {
        const autoRange = viewerHandle.getFieldRangeForModels(childModelIds);
        setRange(autoRange);
      } else {
        setRange(null);
      }
      onFieldApplied?.(fieldName, activeColormapRef.current);
    },
    [viewerHandle, childModelIds, onFieldApplied],
  );

  const handleColormapChange = useCallback(
    (name: string) => {
      if (!viewerHandle) return;
      setActiveColormap(name);
      viewerHandle.setColormapForModels(childModelIds, name);
      onFieldApplied?.(activeFieldRef.current, name);
    },
    [viewerHandle, childModelIds, onFieldApplied],
  );

  const commitRange = useCallback(() => {
    if (!viewerHandle) return;
    const min = parseFloat(localMin);
    const max = parseFloat(localMax);
    if (!isNaN(min) && !isNaN(max) && min < max) {
      viewerHandle.setFieldRangeForModels(childModelIds, min, max);
      setRange([min, max]);
    }
  }, [viewerHandle, childModelIds, localMin, localMax]);

  const handleLogScale = useCallback(
    (on: boolean) => {
      if (!viewerHandle) return;
      setLogScale(on);
      viewerHandle.setLogScaleForModels(childModelIds, on);
    },
    [viewerHandle, childModelIds],
  );

  const handleContourSteps = useCallback(
    (steps: number) => {
      if (!viewerHandle) return;
      setContourSteps(steps);
      viewerHandle.setContourStepsForModels(childModelIds, steps);
    },
    [viewerHandle, childModelIds],
  );

  const handleUseColormap = useCallback(
    (on: boolean) => {
      setUseColormap(on);
      if (on && activeField) {
        applyField(activeField);
      } else if (!on) {
        applyField(undefined);
      }
    },
    [activeField, applyField],
  );

  const handleGlobalRange = useCallback(
    (isGlobal: boolean) => {
      setGlobalRange(isGlobal);
      if (isGlobal && viewerHandle) {
        const autoRange = viewerHandle.getFieldRangeForModels(childModelIds);
        if (autoRange) {
          setRange(autoRange);
          viewerHandle.setFieldRangeForModels(childModelIds, autoRange[0], autoRange[1]);
        }
      }
    },
    [viewerHandle, childModelIds],
  );

  const filteredChildren = useMemo(() => {
    if (!search) return children;
    const q = search.toLowerCase();
    return children.filter((c) => c.label.toLowerCase().includes(q));
  }, [children, search]);

  return (
    <div style={PANEL} className="f3v-iso-panel">
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          borderBottom: "1px solid var(--gray-a4)",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 12 }}>{layer.label}</span>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: "var(--gray-9)",
            cursor: "pointer",
            padding: 2,
            display: "flex",
            alignItems: "center",
          }}
        >
          <CloseIcon />
        </button>
      </div>

      {/* Isosurface field selector */}
      <div style={SECTION}>
        <div style={LABEL}>Isosurface field</div>
        <select
          value={activeField ?? ""}
          onChange={(e) => applyField(e.target.value || undefined)}
          style={SELECT}
        >
          <option value="">None</option>
          {fieldNames.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      </div>

      {/* Isosurfaces list */}
      <div style={{ ...SECTION, maxHeight: 160, overflowY: "auto" }}>
        <div style={LABEL}>Isosurfaces list</div>
        {children.length > 4 && (
          <input
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={SEARCH_INPUT}
          />
        )}
        {filteredChildren.length === 0 && (
          <div style={{ color: "var(--gray-8)", fontSize: 10, padding: "4px 0" }}>
            No isosurfaces
          </div>
        )}
        {filteredChildren.map((child) => {
          const vis = visibility.get(child.id) ?? child.defaultVisible;
          return (
            <button
              key={child.id}
              onClick={() => onToggle(child.id, !vis)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                width: "100%",
                padding: "3px 4px",
                border: "none",
                background: "transparent",
                color: "var(--gray-12)",
                fontSize: 10,
                cursor: "pointer",
                opacity: vis ? 1 : 0.5,
                borderRadius: 3,
                textAlign: "left",
              }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLElement).style.background = "var(--gray-a3)")
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLElement).style.background = "transparent")
              }
            >
              <span style={{ color: vis ? "var(--accent-9)" : "var(--gray-8)", flexShrink: 0 }}>
                {vis ? <EyeOpen /> : <EyeClosed />}
              </span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {child.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Use colormap toggle */}
      <div style={SECTION}>
        <div style={TOGGLE_ROW}>
          <span>Use colormap</span>
          <ToggleSwitch checked={useColormap} onChange={handleUseColormap} />
        </div>
      </div>

      {/* Colormap controls (visible when useColormap is on) */}
      {useColormap && (
        <>
          {/* Log scale */}
          <div style={SECTION}>
            <div style={TOGGLE_ROW}>
              <span>Log scale</span>
              <ToggleSwitch checked={logScale} onChange={handleLogScale} />
            </div>
          </div>

          {/* Colormap selector + Global/Local */}
          <div style={SECTION}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <span style={{ ...LABEL, marginBottom: 0, flex: 1 }}>Colormap</span>
              <div style={{ display: "flex", gap: 2 }}>
                <button
                  onClick={() => handleGlobalRange(true)}
                  style={{
                    fontSize: 9,
                    padding: "2px 6px",
                    borderRadius: 3,
                    border: "1px solid var(--gray-a5)",
                    background: globalRange ? "var(--accent-a4)" : "transparent",
                    color: globalRange ? "var(--accent-11)" : "var(--gray-10)",
                    cursor: "pointer",
                  }}
                >
                  Global
                </button>
                <button
                  onClick={() => handleGlobalRange(false)}
                  style={{
                    fontSize: 9,
                    padding: "2px 6px",
                    borderRadius: 3,
                    border: "1px solid var(--gray-a5)",
                    background: !globalRange ? "var(--accent-a4)" : "transparent",
                    color: !globalRange ? "var(--accent-11)" : "var(--gray-10)",
                    cursor: "pointer",
                  }}
                >
                  Local
                </button>
              </div>
            </div>
            <select
              value={activeColormap ?? ""}
              onChange={(e) => handleColormapChange(e.target.value)}
              style={SELECT}
            >
              {colormaps.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Min / Max */}
          <div style={SECTION}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <label style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
                <span style={{ fontSize: 10, color: "var(--gray-9)" }}>Min</span>
                <input
                  type="number"
                  value={localMin}
                  onChange={(e) => setLocalMin(e.target.value)}
                  onBlur={commitRange}
                  onKeyDown={(e) => e.key === "Enter" && commitRange()}
                  style={NUM_INPUT}
                />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
                <span style={{ fontSize: 10, color: "var(--gray-9)" }}>Max</span>
                <input
                  type="number"
                  value={localMax}
                  onChange={(e) => setLocalMax(e.target.value)}
                  onBlur={commitRange}
                  onKeyDown={(e) => e.key === "Enter" && commitRange()}
                  style={NUM_INPUT}
                />
              </label>
            </div>

            {/* Colormap preview bar */}
            <div
              style={{
                height: 12,
                borderRadius: 3,
                marginTop: 6,
                background: "linear-gradient(to right, #0000ff, #00ffff, #00ff00, #ffff00, #ff0000)",
                opacity: 0.8,
              }}
            />
          </div>
        </>
      )}

      {/* Advanced (collapsible) */}
      <div style={{ ...SECTION, borderBottom: "none" }}>
        <button
          onClick={() => setAdvancedOpen((o) => !o)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            width: "100%",
            background: "none",
            border: "none",
            color: "var(--gray-10)",
            fontSize: 11,
            cursor: "pointer",
            padding: 0,
          }}
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transition: "transform 0.15s ease",
              transform: advancedOpen ? "rotate(90deg)" : "rotate(0deg)",
            }}
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
          Advanced
        </button>
        {advancedOpen && (
          <div style={{ paddingTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 10, color: "var(--gray-9)", minWidth: 48 }}>
                Contour steps
              </span>
              <input
                type="number"
                min={0}
                max={100}
                value={contourSteps}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v >= 0) handleContourSteps(v);
                }}
                style={{ ...NUM_INPUT, width: 50 }}
              />
            </label>
          </div>
        )}
      </div>
    </div>
  );
});
